import { AggregateRatingData } from '../../../hooks/useReviews';
import BarkMeter from '../../../components/feature/BarkMeter';

interface ReviewStatsProps {
  aggregate: AggregateRatingData;
  loading: boolean;
}

/**
 * ReviewStats component renders a summary of review statistics.
 * It safely handles missing or malformed data and provides a loading
 * skeleton while the data is being fetched.
 */
export default function ReviewStats({ aggregate, loading }: ReviewStatsProps) {
  /**
   * Defensive data extraction – ensures we never read undefined
   * properties which could cause a runtime error.
   */
  const ratingCounts = aggregate?.ratingCounts ?? {};
  const reviewCount = aggregate?.reviewCount ?? 0;
  const ratingValue = aggregate?.ratingValue ?? 0;
  const mediaCount = aggregate?.mediaCount ?? 0;

  // Build rating bars for 5‑to‑1 stars
  const ratingBars = [5, 4, 3, 2, 1].map((stars) => ({
    stars,
    count: Number(ratingCounts[stars]) || 0,
  }));

  // Percentage of reviews that are 4 or 5 stars
  const recommendPct =
    reviewCount > 0
      ? Math.round(
          ((Number(ratingCounts[4]) || 0) + (Number(ratingCounts[5]) || 0)) /
            reviewCount *
            100,
        )
      : 0;

  // Percentage of reviews that are 4+ stars (used in quick stats)
  const fourPlusPct =
    reviewCount > 0
      ? Math.round(
          (ratingBars
            .filter((b) => b.stars >= 4)
            .reduce((sum, b) => sum + b.count, 0) /
            reviewCount) *
            100,
        )
      : 0;

  if (loading) {
    return (
      <section className="bg-white rounded-2xl p-6 sm:p-8 shadow-sm mb-10 animate-pulse">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
          <div className="text-center">
            <div className="h-12 w-16 bg-gray-200 rounded mx-auto mb-2" />
            <div className="h-5 w-24 bg-gray-200 rounded mx-auto mb-2" />
            <div className="h-4 w-32 bg-gray-100 rounded mx-auto" />
          </div>
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-3 bg-gray-100 rounded" />
            ))}
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-20 bg-gray-100 rounded-xl" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section aria-label="Review statistics summary" className="bg-white rounded-2xl p-4 sm:p-6 md:p-8 shadow-sm mb-6 sm:mb-10">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 items-center">
        {/* Overall Rating */}
        <div className="text-center">
          <div className="text-4xl sm:text-5xl font-bold text-gray-900 mb-2">{ratingValue}</div>
          <div className="flex items-center justify-center mb-2">
            <BarkMeter rating={parseFloat(String(ratingValue))} size="lg" />
          </div>
          <p className="text-sm text-[#374151] mb-1">Based on {reviewCount} reviews</p>
          <p className="text-xs text-[#374151] italic" aria-hidden="true">We rate in paws, not stars 🐾</p>
        </div>

        {/* Rating Breakdown */}
        <ul className="space-y-2" aria-label="Rating breakdown">
          {ratingBars.map((bar) => (
            <li
              key={bar.stars}
              className="flex items-center gap-3"
            >
              <span className="text-sm text-[#374151] w-14 whitespace-nowrap flex items-center gap-1">
                <span>{bar.stars}</span>
                <span aria-hidden="true">🐾</span>
                <span className="sr-only">paws</span>
              </span>
              <div
                className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden"
                role="progressbar"
                aria-valuenow={reviewCount > 0 ? Math.round((bar.count / reviewCount) * 100) : 0}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={`${bar.stars} paw reviews: ${bar.count}`}
              >
                <div
                  className="h-full bg-teal-500 rounded-full transition-all duration-500"
                  style={{
                    width: `${reviewCount > 0 ? (bar.count / reviewCount) * 100 : 0}%`,
                  }}
                />
              </div>
              <span className="text-sm text-[#374151] w-6 text-right">{bar.count}</span>
            </li>
          ))}
        </ul>

        {/* Quick Stats */}
        <ul className="grid grid-cols-2 gap-3 sm:gap-4" aria-label="Quick review statistics">
          <li className="bg-teal-50 rounded-xl p-3 sm:p-4 text-center">
            <div className="text-xl sm:text-2xl font-bold text-teal-700">{mediaCount}</div>
            <p className="text-xs text-teal-700 mt-1 font-medium">With Photos/Videos</p>
          </li>

          <li className="bg-amber-50 rounded-xl p-3 sm:p-4 text-center">
            <div className="text-xl sm:text-2xl font-bold text-amber-700">{recommendPct}%</div>
            <p className="text-xs text-amber-700 mt-1 font-medium">Recommend Us</p>
          </li>

          <li className="bg-green-50 rounded-xl p-3 sm:p-4 text-center">
            <div className="text-xl sm:text-2xl font-bold text-green-700">{fourPlusPct}%</div>
            <p className="text-xs text-green-700 mt-1 font-medium">4+ Paw Reviews</p>
          </li>

          <li className="bg-gray-50 rounded-xl p-3 sm:p-4 text-center">
            <div className="text-xl sm:text-2xl font-bold text-[#374151]">{ratingValue}</div>
            <p className="text-xs text-[#374151] mt-1 font-medium">Avg Bark Score</p>
          </li>
        </ul>
      </div>
    </section>
  );
}
